import {
  Avatar,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material'
import debug from 'debug'
import { onMessage } from 'firebase/messaging'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { dialog } from 'src/components/Modal/DynamicModal'
import { executeSetPushToken } from 'src/services/apollo/auth'
import IcBaselineLock from '~icons/ic/baseline-lock'
import IcBaselineNotifications from '~icons/ic/baseline-notifications'
import IcBaselineSettings from '~icons/ic/baseline-settings'
import {
  getFirebaseMessaging,
  getFirebaseToken,
} from '../services/firebase/messaging'
import {
  attemptParseJSON,
  isNotificationAvailable,
  isServiceWorkerAvailable,
} from '.'
import { getDisablePush, setDisablePush } from './idb'
import { getToken } from './token'
import { track } from './tracker'

const log = debug('pixai:swClient')

if (isServiceWorkerAvailable)
  navigator.serviceWorker.register('/sw.js').then(reg => {
    reg.active?.postMessage({
      type: 'client-sync',
      debug: localStorage.getItem('debug'),
    })
  })

export async function refreshPushToken(
  requestPermission = false,
  scene?: string,
): Promise<boolean> {
  if (!isNotificationAvailable) return false

  if (requestPermission) {
    const permission = await Notification.requestPermission().catch(
      () => 'default',
    )
    if (permission === 'granted') {
      track('notification_granted', { scene })
      localStorage.removeItem('notification::dismissed-inbox-permission-prompt')
      localStorage.removeItem(
        'notification::dismissed-generation-submit-permission-prompt',
      )
      await setDisablePush(false)
    } else {
      if (permission === 'denied') {
        track('notification_denied', { scene })
      }

      track('notification_unblock_open', { scene })
      const dialogRef = dialog({
        children: (
          <EnableNotificationContent close={() => dialogRef.current!.close()} />
        ),
        onClose: () => {
          track('notification_unblock_close', { scene })
        },
      })
    }
  }

  if (!isNotificationAvailable || Notification.permission !== 'granted')
    return false

  if (await getDisablePush()) return false

  let token
  try {
    token = await getFirebaseToken()
  } catch (e) {
    track('push_token_sync', { scene, success: false })
    return false
  }
  track('push_token_sync', { scene, success: true })

  log('FCM registration token', token)

  if (!token) return false

  const res = await executeSetPushToken({ token })
  return res.data?.setPushToken?.acknowledged || false
}

function EnableNotificationContent({ close }: { close: () => void }) {
  const { t } = useTranslation(['common'])

  return (
    <>
      <DialogTitle>
        {t('common:notification.unblock-tutorial.title')}
      </DialogTitle>
      <DialogContent>
        <List>
          <ListItem>
            <ListItemAvatar>
              <Avatar>1</Avatar>
            </ListItemAvatar>
            <ListItemText>
              <Trans
                t={t}
                i18nKey="common:notification.unblock-tutorial.step-1"
                components={{
                  PadlockIcon: <IcBaselineLock className="inline-block" />,
                }}
              />
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemAvatar>
              <Avatar>2</Avatar>
            </ListItemAvatar>
            <ListItemText>
              <Trans
                t={t}
                i18nKey="common:notification.unblock-tutorial.step-2"
                components={{
                  SettingsIcon: <IcBaselineSettings className="inline-block" />,
                  Reference: <span className="font-semibold" />,
                }}
              />
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemAvatar>
              <Avatar>3</Avatar>
            </ListItemAvatar>
            <ListItemText>
              <Trans
                t={t}
                i18nKey="common:notification.unblock-tutorial.step-3"
                components={{
                  NotificationsIcon: (
                    <IcBaselineNotifications className="inline-block" />
                  ),
                  Reference: <span className="font-semibold" />,
                }}
              />
            </ListItemText>
          </ListItem>
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} autoFocus>
          {t('common:notification.unblock-tutorial.confirm')}
        </Button>
      </DialogActions>
    </>
  )
}

let lastUserId: string | undefined
export function refreshPushTokenEffect(userId: string) {
  if (userId === lastUserId) return
  lastUserId = userId
  refreshPushToken()
}

export async function withdrawPushToken(scene?: string) {
  if (!isNotificationAvailable || Notification.permission !== 'granted') return

  let token
  try {
    token = await getFirebaseToken()
  } catch (e) {
    track('withdraw_push_token', { scene, success: false })
    return
  }
  track('withdraw_push_token', { scene, success: true })

  if (token) await executeSetPushToken({ token, withdraw: true })

  lastUserId = undefined
}

export async function isPushEnabled() {
  if (!isNotificationAvailable || Notification.permission !== 'granted')
    return false

  if (!getToken()) return false

  if (await getDisablePush()) return false

  let token
  try {
    token = await getFirebaseToken()
  } catch (e) {
    return false
  }

  return !!token
}

getFirebaseMessaging().then(messaging => {
  if (!messaging) return
  onMessage(messaging, async msgPayload => {
    log('Received push message', msgPayload)
    const { data } = msgPayload
    if (!data) return
    const type = data.type || 'notification'
    switch (type) {
      case 'notification': {
        if (!data.title) return

        const webPushOpts = attemptParseJSON(data.webpush)
        const payload = attemptParseJSON(data.payload)
        const opts = { ...data, ...webPushOpts }

        const disablePush = await getDisablePush()
        if (!disablePush) {
          toast.info(
            <div className="flex gap-2">
              {(opts.icon || opts.image) && (
                <img
                  className="w-12 h-12 rounded object-cover object-center"
                  src={opts.icon || opts.image}
                  alt=""
                />
              )}

              <div>
                <div>{opts.title}</div>
                <p className="text-sm line-clamp-2">{opts.body}</p>
              </div>
            </div>,
            {
              icon: false,
              onClick: () => {
                const payloadType = 'type' in payload ? payload.type : undefined
                track('notification_click', {
                  inApp: true,
                  type: payloadType,
                })

                // ensure the link is from the same origin
                const url = new URL(opts.link, location.href)
                const originUrl = new URL(location.origin)
                if (url.host !== originUrl.host) {
                  url.protocol = originUrl.protocol
                  url.host = originUrl.host
                  log(
                    'The link is not from the same origin, opening anyway.\n',
                    data.link,
                    '=>',
                    url.href,
                  )
                }

                window.open(url.href, '_blank')
              },
              autoClose: 15 * 1000,
              pauseOnFocusLoss: false,
            },
          )
          track('notification_impression', {
            inApp: true,
            type: 'type' in payload ? payload.type : undefined,
          })
        }

        track('notification_received', {
          inApp: true,
          type: 'type' in payload ? payload.type : undefined,
          disablePush,
        })

        break
      }
      default: {
        log('Received unknown push message', msgPayload)
        break
      }
    }
  })
})
